/**
 * Studios Page - Simplified with My Submissions
 * ==============================================
 * Clean 2x2 grid layout showing available studios.
 * Shows My Submissions below for Supervisor/Management users.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { EMZI_COLORS, EMZI_SHADOWS } from '../styles/emziCI';
import { getMySubmissions } from '../api/contentStudioV2';
import StatusBadge from '../components/common/StatusBadge';
import { secureStorage } from '../utils/secureStorage';

const Studios = () => {
  const navigate = useNavigate();
  const { user, isSupervisor, isManagement, isAdmin } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);

  // Check if user should see submissions list
  const showSubmissions = isSupervisor() || isManagement() || isAdmin();

  // Studio configuration - only Content is unlocked
  const studios = [
    {
      id: 'content',
      name: 'Content',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      path: '/studio/content-v2',
      locked: false
    },
    {
      id: 'live',
      name: 'Live',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
        </svg>
      ),
      path: '/studio/live-v2',
      locked: false
    },
    {
      id: 'copy',
      name: 'Copy',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      path: '/studio/copy',
      locked: true
    },
    {
      id: 'analyst',
      name: 'Analyst',
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
      path: '/studio/analyst',
      locked: true
    }
  ];

  // Load submissions for supervisor/management
  const loadSubmissions = useCallback(async () => {
    try {
      setLoadingSubmissions(true);
      const token = secureStorage.getItem('nexus_access_token');
      const data = await getMySubmissions(token, user.user_id);
      setSubmissions(data.submissions || []);
    } catch (error) {
      console.error('Failed to load submissions:', error);
    } finally {
      setLoadingSubmissions(false);
    }
  }, [user?.user_id]);

  useEffect(() => {
    if (showSubmissions && user?.user_id) {
      loadSubmissions();
    }
  }, [showSubmissions, user?.user_id, loadSubmissions]);

  const handleStudioClick = (studio) => {
    if (studio.locked) return;
    navigate(studio.path);
  };

  const handleSubmissionClick = (submission) => {
    // Navigate to submission detail or video submit page
    if (submission.submission_status === 'approved' && !submission.video_url) {
      navigate(`/my-submissions?submit_video=${submission.session_id}`);
    } else {
      navigate(`/my-submissions?view=${submission.session_id}`);
    }
  };

  // Get status badge color
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return { bg: '#16A34A', text: 'white' };
      case 'pending_approval': return { bg: '#F59E0B', text: 'white' };
      case 'production_complete': return { bg: '#8B5CF6', text: 'white' };
      default: return { bg: '#6B7280', text: 'white' };
    }
  };

  // Get status display text
  const getStatusText = (submission) => {
    if (submission.video_status === 'approved') return '✅ Siap';
    if (submission.submission_status === 'approved' && !submission.video_url) return '🎬 Upload Video';
    if (submission.submission_status === 'approved') return '⏳ Video Review';
    if (submission.submission_status === 'pending_approval') return '⏳ Pending';
    return submission.submission_status || 'Draft';
  };

  // Count unlocked studios
  const unlockedCount = studios.filter(s => !s.locked).length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: EMZI_COLORS.surface }}>
      {/* Page Header */}
      <div 
        className="sticky top-0 z-20 px-4 py-3 border-b" 
        style={{ 
          backgroundColor: EMZI_COLORS.primary,
          borderColor: EMZI_COLORS.border 
        }}
      >
        <h1 className="text-lg font-bold text-white flex items-center gap-2">
          🎬 Studios
        </h1>
        <p className="text-xs text-white/80">
          Pilih studio untuk mula mencipta kandungan
        </p>
      </div>

      {/* Content Area */}
      <div className="p-3 space-y-3">
        {/* Studios Card */}
        <div 
          className="rounded-2xl p-4"
          style={{ 
            backgroundColor: EMZI_COLORS.primary,
            boxShadow: EMZI_SHADOWS.lg
          }}
        >
          {/* Card Header */}
          <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">🎬</span>
            <h2 className="text-base font-bold text-white">Creative Studios</h2>
            <Badge 
              className="text-xs font-bold px-2 py-0.5"
              style={{ backgroundColor: 'white', color: EMZI_COLORS.primary }}
            >
              {unlockedCount}
            </Badge>
          </div>

          {/* 2x2 Grid of Studios */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {studios.map((studio) => (
              <div
                key={studio.id}
                onClick={() => handleStudioClick(studio)}
                className="relative rounded-xl p-4 text-center transition-all duration-200"
                style={{
                  backgroundColor: studio.locked ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.2)',
                  cursor: studio.locked ? 'not-allowed' : 'pointer',
                  border: studio.locked ? 'none' : '2px solid rgba(255,255,255,0.3)',
                  opacity: studio.locked ? 0.6 : 1
                }}
              >
                {/* Lock Icon for locked studios */}
                {studio.locked && (
                  <div className="absolute top-2 right-2">
                    <svg className="w-4 h-4 text-white/50" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
                
                {/* Icon */}
                <div className="flex justify-center mb-2">
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ 
                      backgroundColor: studio.locked ? 'rgba(255,255,255,0.1)' : 'rgba(255,255,255,0.25)',
                      color: 'white'
                    }}
                  >
                    {studio.icon}
                  </div>
                </div>
                
                {/* Name */}
                <div 
                  className="text-sm font-semibold"
                  style={{ color: studio.locked ? 'rgba(255,255,255,0.5)' : 'white' }}
                >
                  {studio.name}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* My Submissions Section - Only for Supervisor/Management */}
        {showSubmissions && (
          <Card style={{ borderColor: EMZI_COLORS.border }}>
            <CardHeader className="px-3 py-2 border-b" style={{ backgroundColor: EMZI_COLORS.surface }}>
              <CardTitle className="text-sm font-bold flex items-center gap-2">
                📋 My Submissions
                <Badge 
                  className="text-xs px-1.5 py-0"
                  style={{ backgroundColor: EMZI_COLORS.primary, color: 'white' }}
                >
                  {submissions.length}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3">
              {loadingSubmissions ? (
                <div className="text-center py-4">
                  <div 
                    className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin mx-auto"
                    style={{ borderColor: `${EMZI_COLORS.primary} transparent` }}
                  ></div>
                  <p className="text-xs text-gray-500 mt-2">Loading...</p>
                </div>
              ) : submissions.length === 0 ? (
                <div className="text-center py-6">
                  <div className="text-3xl mb-2">📝</div>
                  <p className="text-sm text-gray-500">Tiada submission lagi</p>
                  <p className="text-xs text-gray-400 mt-1">Mula mencipta di Content Studio</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {submissions.slice(0, 5).map((submission) => {
                    const statusColor = getStatusColor(submission.submission_status);
                    return (
                      <div
                        key={submission.session_id}
                        onClick={() => handleSubmissionClick(submission)}
                        className="flex items-center justify-between p-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                        style={{ backgroundColor: 'white', border: `1px solid ${EMZI_COLORS.border}` }}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-sm text-gray-800 truncate">
                            {submission.project_name || 'Untitled Project'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {submission.platform_context?.channels?.join(', ') || 'Content'} • 
                            {submission.submitted_at ? new Date(submission.submitted_at).toLocaleDateString('ms-MY') : '—'}
                          </div>
                        </div>
                        <Badge 
                          className="text-[10px] px-2 py-0.5 ml-2"
                          style={{ backgroundColor: statusColor.bg, color: statusColor.text }}
                        >
                          {getStatusText(submission)}
                        </Badge>
                      </div>
                    );
                  })}
                  
                  {/* View All Link */}
                  {submissions.length > 5 && (
                    <div 
                      className="text-center pt-2"
                      onClick={() => navigate('/my-submissions')}
                    >
                      <span className="text-xs text-blue-600 cursor-pointer hover:underline">
                        Lihat semua ({submissions.length}) →
                      </span>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Bottom spacer for nav bar */}
      <div className="h-20"></div>
    </div>
  );
};

export default Studios;
